import type { CSSProperties } from "react";

/**
 * The type blocks the You stack repeats, from
 * `docs/design/handoff/design/templates/you/you-screen.jsx` (`kick`, `t1`,
 * `t2`, `PAD`, `hr`) and `04-YOU.md`.
 *
 * Here rather than copied into eight files: root, reading, archetype, edit,
 * deleted, shared and error are the same three or four type sizes in
 * different arrangements, and the handoff's numbers should have one home.
 * Anything that differs between the screens stays in the screen.
 */

/** The screen gutter — 24 either side, and nothing else. */
export const GUTTER: CSSProperties = { padding: "0 var(--space-6)" };

/** H2 on the root, the reading and the archetype: `400 34px/38px Belleza`.
 *
 * No `text-wrap: balance` (B20): no template balances a heading — the copy
 * breaks at its `<span>` split, and balancing moves that break. */
export const TITLE: CSSProperties = {
  font: "400 34px/38px var(--font-display)",
  color: "var(--d-fg)",
  margin: 0,
};

/** H2 on the quieter screens — edit, deleted, shared, error: `400 30px/36px`. */
export const TITLE_SM: CSSProperties = {
  font: "400 30px/36px var(--font-display)",
  color: "var(--d-fg)",
  margin: 0,
};

/** The second clause of a two-clause headline, dimmed. */
export const TITLE_DIM: CSSProperties = { color: "var(--d-fg-3)" };

/** Body: `500 14px/21px Quicksand`, `--d-fg-2`. */
export const LEAD: CSSProperties = {
  font: "500 14px/21px var(--font-body)",
  color: "var(--d-fg-2)",
  margin: 0,
  textWrap: "pretty",
};

/** A block's title line — the handoff's `t1`. */
export const T1: CSSProperties = {
  font: "600 15px/20px var(--font-body)",
  color: "var(--d-fg)",
};

/** A block's description — the handoff's `t2`. One pixel of leading off
 * `--t-caption`, which is a different token for a different thing. */
export const T2: CSSProperties = {
  font: "500 13px/19px var(--font-body)",
  color: "var(--d-fg-2)",
  textWrap: "pretty",
};

/** A Belleza line inside a block — the reading's and the shared view's
 * headline: `400 22px/28px`. */
export const HEADLINE: CSSProperties = {
  font: "400 22px/28px var(--font-display)",
  color: "var(--d-fg)",
  textWrap: "pretty",
};

/** The mono tag on the right of a chapter block: `☉ LEO`, `☉ □ ☽`. */
export const MONO_TAG: CSSProperties = {
  font: "400 10px/14px var(--font-mono)",
  letterSpacing: ".06em",
  color: "var(--d-fg-4)",
};

/** The mono identity stamp under THE BIG THREE: `400 11px/16px`, .04em. */
export const MONO_STAMP: CSSProperties = {
  font: "400 11px/16px var(--font-mono)",
  letterSpacing: ".04em",
  color: "var(--d-fg-3)",
};

/** The shared view's glyph line: `400 12px/18px`, .02em. */
export const MONO_GLYPHS: CSSProperties = {
  font: "400 12px/18px var(--font-mono)",
  letterSpacing: ".02em",
  color: "var(--d-fg-3)",
};

/** 05-SHARED.md §"Состояния": one mono line, no spinner, ever. */
export const MONO_STATE: CSSProperties = {
  font: "400 11px/16px var(--font-mono)",
  letterSpacing: ".1em",
  color: "var(--d-fg-3)",
};

/**
 * **The only sanctioned red in the product** (04-YOU.md §Settings): the
 * "Delete account" action and the confirm sheet's destructive button. It is
 * written as a literal `oklch()` rather than a token on purpose — a token
 * would invite a second use, and there is exactly one.
 */
export const DANGER = "oklch(62% 0.17 27)";
export const DANGER_BORDER = "oklch(62% 0.17 27 / .5)";
